import i18n from '@/i18n';
import { ApiError, type ApiErrorCode } from '@/api/errors';
import { toast } from '@/hooks/useToast';

/** Cap on rendered field violations per toast — keeps the toast bounded for huge payloads. */
const MAX_FIELD_VIOLATIONS_IN_TOAST = 5;

/**
 * Translates an unknown error into a user-facing toast.
 *
 * Per `src/api/errors.ts`, the raw `Error.message` may include PHI from
 * downstream services and must never be rendered. The toast title is keyed
 * off the stable `ApiError.code`; the description references the
 * correlationId when available so support can match a report to a log.
 *
 * `validation_failed` is the one case where per-field text *is* shown — those
 * messages come from Hibernate Validator (constraint message templates), not
 * downstream services, and the per-field shape lets the user actually fix the
 * thing they typed.
 *
 * `aborted` errors are skipped — they fire on user-initiated cancellation
 * (route changes, dialog dismissals) and surfacing a toast there would be
 * noise. `unauthorized` is also skipped because the SessionGuard already
 * handles 401s with a full-screen sign-in prompt.
 */
export function notifyApiError(error: unknown): void {
  if (!(error instanceof ApiError)) {
    toast({
      variant: 'destructive',
      title: i18n.t('error.toast.title.unknown', { ns: 'common' }),
      description: i18n.t('error.toast.description.default', { ns: 'common' }),
    });
    return;
  }

  if (error.code === 'aborted' || error.code === 'unauthorized') return;

  if (error.code === 'validation_failed' && error.fieldViolations !== undefined) {
    const lines = error.fieldViolations
      .slice(0, MAX_FIELD_VIOLATIONS_IN_TOAST)
      .map((v) => `${v.field}: ${v.message}`);
    const truncated = error.fieldViolations.length - lines.length;
    if (truncated > 0) {
      lines.push(
        i18n.t('error.toast.description.moreViolations', {
          ns: 'common',
          count: truncated,
        }),
      );
    }
    toast({
      variant: 'destructive',
      title: i18n.t('error.toast.title.validation_failed', { ns: 'common' }),
      description: lines.join('\n'),
    });
    return;
  }

  const titleKey: ApiErrorCode = error.code;
  const description = buildDescription(error);

  toast({
    variant: 'destructive',
    title: i18n.t(`error.toast.title.${titleKey}`, { ns: 'common' }),
    description,
  });
}

/**
 * Prefer the JSON:API `detail` string when the BE surfaced one — it's
 * intentionally user-facing (e.g. "Person already belongs to family X") and
 * far more actionable than the generic default. Append a correlation-id line
 * when present so support can find the log. Fall back to the generic default
 * copy for errors that don't carry a detail.
 */
function buildDescription(error: ApiError): string {
  if (error.userFacingDetail !== undefined) {
    if (error.correlationId !== undefined) {
      return `${error.userFacingDetail}\n${i18n.t('error.toast.description.referenceOnly', {
        ns: 'common',
        correlationId: error.correlationId,
      })}`;
    }
    return error.userFacingDetail;
  }
  if (error.correlationId !== undefined) {
    return i18n.t('error.toast.description.withReference', {
      ns: 'common',
      correlationId: error.correlationId,
    });
  }
  return i18n.t('error.toast.description.default', { ns: 'common' });
}
